/**
 * Export Panel Component
 * 
 * Reusable drawer panel for exporting table data
 * Features:
 * - Format selection (Excel, PDF, CSV)
 * - Column selection with checkboxes (disabled for CSV - exports all columns)
 * - Preview file name with timestamp
 * - Export and Cancel buttons
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Radio, Checkbox, Button, message } from 'antd';
import type { RadioChangeEvent } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { DownloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import RightContentDrawer from './RightContentDrawer';
import ButtonGroup from './ButtonGroup';
import { exportData, type ExportFormat, type ExportColumn, type MetadataRow } from '../../utils/exportUtils';
import type { RootState } from '../../store';

// ========== TYPES ==========

export interface ExportColumnConfig {
  key: string;
  title: string;
  width?: number;
  defaultSelected?: boolean;
}

export interface ExportPanelProps<T extends Record<string, unknown>> {
  open: boolean;
  onClose: () => void;
  data: T[];
  columns: ExportColumnConfig[];
  fileName: string;
  title?: string;
  sheetName?: string;
  formats?: ExportFormat[];
  metadataRows?: MetadataRow[];
}

// ========== STYLED COMPONENTS ==========

const PanelContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div``;

const SectionTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
  margin-bottom: 12px;
`;

const FormatGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormatOption = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid ${props => props.$selected ? '#2563EB' : '#E5E7EB'};
  border-radius: 8px;
  background: ${props => props.$selected ? '#EFF6FF' : '#FFFFFF'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #2563EB;
  }

  .ant-radio-wrapper {
    width: 100%;
    margin: 0;
  }
`;

const FormatLabel = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
  margin-left: 8px;
`;

const FormatExt = styled.span`
  font-family: Inter;
  font-size: 12px;
  color: #6B7280;
  margin-left: 4px;
`;

const ColumnSection = styled.div<{ $disabled?: boolean }>`
  opacity: ${props => props.$disabled ? 0.5 : 1};
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
`;

const ColumnList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 8px;
`;

const ColumnItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;

  &:hover {
    background: #F9FAFB;
  }

  .ant-checkbox-wrapper {
    width: 100%;
    
    span:last-child {
      font-family: Inter;
      font-size: 14px;
      color: #1F2937;
    }
  }
`;

const SelectAllRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #E5E7EB;
  margin-bottom: 4px;

  .ant-checkbox-wrapper {
    span:last-child {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #1F2937;
    }
  }
`;

const SelectedCount = styled.span`
  font-family: Inter;
  font-size: 12px;
  color: #6B7280;
`;

const DisabledNote = styled.div`
  font-family: Inter;
  font-size: 12px;
  color: #9CA3AF;
  margin-top: 8px;
  font-style: italic;
`;

const FilePreview = styled.div`
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 12px 16px;
`;

const FilePreviewLabel = styled.div`
  font-family: Inter;
  font-size: 12px;
  color: #6B7280;
  margin-bottom: 4px;
`;

const FilePreviewName = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
  word-break: break-all;
`;

const FooterButtonGroup = styled(ButtonGroup)`
  margin-bottom: 0;
  
  .ant-btn {
    flex: 1;
    height: 44px;
  }
`;

// ========== COMPONENT ==========

function ExportPanel<T extends Record<string, unknown>>({
  open,
  onClose,
  data,
  columns,
  fileName,
  title,
  sheetName,
  formats,
  metadataRows,
}: ExportPanelProps<T>): React.ReactElement {
  const { user } = useSelector((state: RootState) => state.auth);

  // State
  const [format, setFormat] = useState<ExportFormat>('excel');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  // Check if CSV format is selected (column selection disabled)
  const isCSVFormat = format === 'csv';

  // Initialize selected columns when panel opens
  useEffect(() => {
    if (open) {
      const defaultSelected = columns
        .filter(col => col.defaultSelected !== false)
        .map(col => col.key);
      setSelectedColumns(defaultSelected);
      setFormat('excel');
    }
  }, [open, columns]);

  // When CSV is selected, automatically select all columns
  useEffect(() => {
    if (isCSVFormat) {
      setSelectedColumns(columns.map(col => col.key));
    }
  }, [isCSVFormat, columns]);

  // Get file extension based on format
  const getExtension = useCallback((fmt: ExportFormat): string => {
    switch (fmt) {
      case 'excel': return '.xlsx';
      case 'pdf': return '.pdf';
      case 'csv': return '.csv';
      default: return '';
    }
  }, []);

  // Preview file name with timestamp
  const previewFileName = useMemo(() => {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_');
    return `${fileName}_${timestamp}${getExtension(format)}`;
  }, [fileName, format, getExtension]);

  // Handle format change
  const handleFormatChange = useCallback((e: RadioChangeEvent) => {
    setFormat(e.target.value as ExportFormat);
  }, []);

  // Handle column toggle
  const handleColumnToggle = useCallback((key: string, checked: boolean) => {
    setSelectedColumns(prev => 
      checked ? [...prev, key] : prev.filter(k => k !== key)
    );
  }, []);

  // Handle select all
  const handleSelectAll = useCallback((e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      setSelectedColumns(columns.map(col => col.key));
    } else {
      setSelectedColumns([]);
    }
  }, [columns]);

  // Check if all selected
  const isAllSelected = selectedColumns.length === columns.length;
  const isIndeterminate = selectedColumns.length > 0 && selectedColumns.length < columns.length;

  // Handle export
  const handleExport = useCallback(async () => {
    if (selectedColumns.length === 0) {
      message.warning('Please select at least one column to export');
      return;
    }

    setExporting(true);
    try {
      // Get selected column configs
      const exportColumns: ExportColumn[] = columns
        .filter(col => selectedColumns.includes(col.key))
        .map(col => ({
          key: col.key,
          title: col.title,
          width: col.width,
        }));

      // Export data (PDF: company branding from Redux)
      await exportData(format, data, exportColumns, {
        fileName,
        title,
        sheetName,
        metadataRows,
        companyLogoUrl: format === 'pdf' ? user?.companyImageUrl : undefined,
        companyName: format === 'pdf' ? user?.companyName : undefined,
      });

      message.success(`Exported ${data.length} rows to ${format.toUpperCase()}`);
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
      message.error('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [selectedColumns, columns, format, data, fileName, title, sheetName, metadataRows, user, onClose]);

  // Footer - following FinancialStatementItemDrawer button style
  const footer = (
    <FooterButtonGroup>
      <Button type="default" onClick={onClose} disabled={exporting}>
        Go back
      </Button>
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        onClick={handleExport}
        loading={exporting}
        disabled={selectedColumns.length === 0}
      >
        Export
      </Button>
    </FooterButtonGroup>
  );

  return (
    <RightContentDrawer
      open={open}
      title="Export Data"
      onClose={onClose}
      width={500}
      footer={footer}
      resizable={true}
    >
      <PanelContent>
        {/* File Format Selection */}
        <Section>
          <SectionTitle>File Format</SectionTitle>
          <Radio.Group value={format} onChange={handleFormatChange} style={{ width: '100%' }}>
            <FormatGroup>
              {(!formats || formats.includes('excel')) && (
                <FormatOption $selected={format === 'excel'}>
                  <Radio value="excel">
                    <FormatLabel>Excel</FormatLabel>
                    <FormatExt>(.xlsx)</FormatExt>
                  </Radio>
                </FormatOption>
              )}
              {(!formats || formats.includes('pdf')) && (
                <FormatOption $selected={format === 'pdf'}>
                  <Radio value="pdf">
                    <FormatLabel>PDF</FormatLabel>
                    <FormatExt>(.pdf)</FormatExt>
                  </Radio>
                </FormatOption>
              )}
              {(!formats || formats.includes('csv')) && (
                <FormatOption $selected={format === 'csv'}>
                  <Radio value="csv">
                    <FormatLabel>CSV</FormatLabel>
                    <FormatExt>(.csv)</FormatExt>
                  </Radio>
                </FormatOption>
              )}
            </FormatGroup>
          </Radio.Group>
        </Section>

        {/* Column Selection */}
        <Section>
          <SectionTitle>Select Columns</SectionTitle>
          <ColumnSection $disabled={isCSVFormat}>
            <ColumnList>
              <SelectAllRow>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                  disabled={isCSVFormat}
                >
                  Select All
                </Checkbox>
                <SelectedCount>
                  {selectedColumns.length} of {columns.length} selected
                </SelectedCount>
              </SelectAllRow>
              {columns.map(col => (
                <ColumnItem key={col.key}>
                  <Checkbox
                    checked={selectedColumns.includes(col.key)}
                    onChange={(e: CheckboxChangeEvent) => handleColumnToggle(col.key, e.target.checked)}
                    disabled={isCSVFormat}
                  >
                    {col.title}
                  </Checkbox>
                </ColumnItem>
              ))}
            </ColumnList>
          </ColumnSection>
          {isCSVFormat && (
            <DisabledNote>
              CSV format exports all columns. Select Excel or PDF to customize columns.
            </DisabledNote>
          )}
        </Section>

        {/* File Preview */}
        <Section>
          <SectionTitle>File Preview</SectionTitle>
          <FilePreview>
            <FilePreviewLabel>File name</FilePreviewLabel>
            <FilePreviewName>{previewFileName}</FilePreviewName>
          </FilePreview>
        </Section>
      </PanelContent>
    </RightContentDrawer>
  );
}

export default ExportPanel;
